const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const roundCurrency = (value) => Math.round((toNumber(value) + Number.EPSILON) * 100) / 100;

const allocateLineDiscounts = (lineAmounts = [], totalDiscount = 0) => {
  const amounts = lineAmounts.map((amount) => Math.max(0, toNumber(amount)));
  const baseTotal = amounts.reduce((sum, amount) => sum + amount, 0);
  const discount = roundCurrency(Math.min(Math.max(0, toNumber(totalDiscount)), baseTotal));

  if (discount <= 0 || baseTotal <= 0 || amounts.length === 0) {
    return amounts.map(() => 0);
  }

  let allocated = 0;
  return amounts.map((amount, index) => {
    if (amount <= 0) {
      return 0;
    }
    if (index === amounts.length - 1) {
      return roundCurrency(discount - allocated);
    }
    const share = roundCurrency(discount * (amount / baseTotal));
    allocated = roundCurrency(allocated + share);
    return share;
  });
};

const findMatchingTaxRule = (ratePerNight, taxRules = []) => {
  const price = toNumber(ratePerNight);
  const rules = Array.isArray(taxRules) ? taxRules : [];
  return rules.find((rule) => {
    const min = rule.min_amount == null ? null : toNumber(rule.min_amount);
    const max = rule.max_amount == null ? null : toNumber(rule.max_amount);
    return (min == null || price >= min) && (max == null || price <= max);
  }) || null;
};

/**
 * Estimate GST per room line using configured tax slabs.
 *
 * @param {boolean} gstIncluded - When true, apply GST using configured slabs.
 * @param {boolean} taxInclusive - When true, entered amounts include tax (extract GST).
 *   When false, tax is added on top of the entered amounts (exclusive).
 */
export const calculateRoomNightGstEstimate = ({
  roomLines = [],
  taxRules = [],
  gstIncluded = false,
  taxInclusive = false,
  totalDiscount = 0,
  discountPercentage = 0,
}) => {
  if (!gstIncluded || !Array.isArray(taxRules) || taxRules.length === 0) {
    return {
      totalGST: 0,
      gstPercentage: 0,
      netTaxableTotal: 0,
      lineBreakdown: roomLines.map(() => ({ gstAmount: 0, gstPercentage: 0, netAmount: 0 })),
    };
  }

  let totalGST = 0;
  let taxableTotal = 0;
  let netTaxableTotal = 0;
  let highestSlabPercentage = 0;
  const lineBreakdown = [];
  const lineAmounts = roomLines.map((line) => toNumber(line.amount));
  const baseTotal = lineAmounts.reduce((sum, amount) => sum + amount, 0);
  let discountToAllocate = toNumber(totalDiscount);
  if (discountToAllocate <= 0 && toNumber(discountPercentage) > 0 && baseTotal > 0) {
    discountToAllocate = roundCurrency(baseTotal * (toNumber(discountPercentage) / 100));
  }
  const lineDiscounts = allocateLineDiscounts(lineAmounts, discountToAllocate);

  roomLines.forEach((line, index) => {
    const amount = lineAmounts[index];
    if (amount <= 0) {
      lineBreakdown.push({ gstAmount: 0, gstPercentage: 0, netAmount: 0, lineDiscount: 0 });
      return;
    }

    const ratePerNight = toNumber(line.ratePerNight);
    const taxRule = findMatchingTaxRule(ratePerNight, taxRules);
    if (!taxRule) {
      lineBreakdown.push({ gstAmount: 0, gstPercentage: 0, netAmount: amount, lineDiscount: lineDiscounts[index] || 0 });
      return;
    }

    const discount = lineDiscounts[index] || 0;
    const taxableAmount = roundCurrency(Math.max(0, amount - discount));
    taxableTotal += taxableAmount;

    const percentage = taxRule.tax_percentage == null ? null : toNumber(taxRule.tax_percentage);
    const fixedAmount = taxRule.tax_amount == null ? null : toNumber(taxRule.tax_amount);
    let lineGST = 0;
    let lineRate = 0;
    if (percentage != null && percentage > 0) {
      lineRate = percentage;
      highestSlabPercentage = Math.max(highestSlabPercentage, lineRate);
      if (taxInclusive) {
        lineGST = taxableAmount * (percentage / (100 + percentage));
      } else {
        lineGST = taxableAmount * (percentage / 100);
      }
    } else if (fixedAmount != null && fixedAmount > 0) {
      lineGST = fixedAmount;
    }

    const roundedLineGST = roundCurrency(lineGST);
    const netAmount = taxInclusive
      ? roundCurrency(Math.max(0, taxableAmount - roundedLineGST))
      : roundCurrency(taxableAmount);

    totalGST += roundedLineGST;
    netTaxableTotal += netAmount;
    lineBreakdown.push({
      gstAmount: roundedLineGST,
      gstPercentage: lineRate,
      netAmount,
      lineDiscount: discount,
    });
  });

  const roundedGST = roundCurrency(totalGST);
  const roundedNet = roundCurrency(netTaxableTotal);
  const roundedTaxable = roundCurrency(taxableTotal);

  return {
    totalGST: roundedGST,
    gstPercentage: highestSlabPercentage,
    netTaxableTotal: roundedNet,
    taxableTotal: roundedTaxable,
    lineBreakdown,
  };
};

/** Split gross line totals into net (ex-GST) amounts for inclusive tax display. */
export function applyInclusiveNetSplit(
  {
    roomBasePrice = 0,
    totalExtraAdultCharge = 0,
    totalExtraChildCharge = 0,
    priceBeforeDiscount = 0,
  },
  priceAfterDiscount,
  netAfterDiscount,
  applyInclusive
) {
  const netRatio = applyInclusive && priceAfterDiscount > 0
    ? netAfterDiscount / priceAfterDiscount
    : 1;
  const scale = (value) => roundCurrency((Number(value) || 0) * netRatio);
  return {
    netRatio,
    netRoomBasePrice: scale(roomBasePrice),
    netExtraAdultCharge: scale(totalExtraAdultCharge),
    netExtraChildCharge: scale(totalExtraChildCharge),
    netPriceBeforeDiscount: scale(priceBeforeDiscount),
  };
}
